import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlternarDaBarra, BarraDeLista, BuscaDaBarra, FiltrosAtivos, RodapeDaLista, usePaginacao, type FiltroAtivo,
} from '../../BarraDeLista';
import { Seletor } from '../../Filtros';
import { data, dataHora, nomeDoContato } from '../../formato';
import { useSessao } from '../../sessao';
import { degrauDe, ESTAGIO_DA_OPORTUNIDADE } from '../../situacoes';
import { CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha } from '../../TabelaComFicha';
import { AtualizacaoPausada, useTempoReal } from '../../tempoReal';
import { Carregando, ErroDaApi, Selo, Vazio } from '../../ui';
import { api } from '../../api';
import {
  ESTAGIOS_DO_QUADRO, LINHA, ORIGEM, localDaObra, reais, textoDe, type ItemDaOportunidade, type Pessoa,
} from './crm';
import { JanelaNovaOportunidade } from './JanelasDaOportunidade';
import './oportunidades.css';

/**
 * As oportunidades — onda 550 (Plano 2 · Comercial, Etapa 1, Tarefa 15;
 * spec do Comercial §3.6, §5.1 e §8.3). `/comercial/oportunidades`.
 *
 * Cada pedido no funil, numa lista do padrão da casa (P-LST): a busca, o
 * recorte «Indicadas por mim», o estágio, a linha e — para a diretoria — o
 * responsável, tudo no endereço (P-LST-05). Cada um vê as próprias e as que
 * indicou; a diretoria vê todas (§3.6, «cada pessoa vê os próprios
 * números»); o valor estimado é de quem tem o Comercial (`crm.ver`;
 * decisão do Humberto, 22/10 — a engenharia e o laboratório não chegam aqui).
 *
 * A linha leva à oportunidade (o histórico do cartão, as propostas, as
 * tarefas). «Nova oportunidade» é a criada à mão (I17): o pedido que chegou
 * fora da caixa — e é a única primária da tela.
 *
 * Ao vivo: `comercial.funil` (só ids; a API confere a carteira a cada evento).
 * O quadro em colunas (kanban) é a tela do funil (Tarefa 18).
 */

const PARAMETROS = ['estagio', 'linha', 'responsavel', 'indicadas'] as const;

const semAcento = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function colunas(veValor: boolean): ColunaComFicha<ItemDaOportunidade>[] {
  return [
    {
      chave: 'contato',
      cabeca: <th scope="col">Contato</th>,
      celula: (o) => (
        <th scope="row">
          <Link to={`/comercial/oportunidade/${o.id}`}>{nomeDoContato(o.contato.nome)}</Link>
          <span className="sub">{o.cliente?.nome ?? 'ainda sem cliente'}</span>
        </th>
      ),
    },
    {
      chave: 'estagio',
      cabeca: <th scope="col">Estágio</th>,
      celula: (o) => {
        const e = degrauDe(ESTAGIO_DA_OPORTUNIDADE, o.estagio);
        return <td><Selo tom={e.tom}>{e.texto}</Selo></td>;
      },
    },
    {
      chave: 'linha', detalhe: true,
      cabeca: <th scope="col" className="col-opcional">Linha</th>,
      celula: (o) => <td className="col-opcional">{o.linha ? textoDe(LINHA, o.linha) : <span className="nada">a identificar</span>}</td>,
    },
    {
      chave: 'local', detalhe: true,
      cabeca: <th scope="col" className="col-opcional">Local</th>,
      celula: (o) => <td className="col-opcional">{localDaObra({ municipio: o.local.municipio, uf: o.local.uf })
        || <span className="nada">não informado</span>}</td>,
    },
    {
      chave: 'responsavel',
      cabeca: <th scope="col">Responsável</th>,
      celula: (o) => <td>{o.responsavel?.nome ?? <span className="nada">ninguém ainda</span>}</td>,
    },
    ...(veValor ? [{
      chave: 'valor', detalhe: true,
      cabeca: <th scope="col" className="col-opcional num">Estimativa</th>,
      celula: (o: ItemDaOportunidade) => (
        <td className="col-opcional num">{o.valorEstimado == null ? <span className="nada">—</span> : reais(o.valorEstimado)}</td>
      ),
    }] : []),
    {
      chave: 'proxima', detalhe: true,
      cabeca: <th scope="col" className="col-opcional">Próxima ação</th>,
      celula: (o) => (
        <td className="col-opcional">
          {o.proximaAcao ? <>{o.proximaAcao}{o.proximaAcaoEm && <span className="sub">{dataHora(o.proximaAcaoEm)}</span>}</>
            : <span className="nada">nenhuma</span>}
        </td>
      ),
    },
  ];
}

function ficha(o: ItemDaOportunidade): FichaDaLinha {
  return {
    rotulo: 'Oportunidade',
    titulo: nomeDoContato(o.contato.nome),
    sub: o.cliente?.nome ?? 'ainda sem cliente',
    corpo: (
      <CamposDaFicha itens={[
        ['Linha', o.linha ? textoDe(LINHA, o.linha) : 'a identificar'],
        ['Local', localDaObra(o.local) || 'não informado'],
        ['Origem', textoDe(ORIGEM, o.origem)],
        ['No estágio desde', data(o.estagioDesde)],
        ['Indicada por', o.indicadaPor?.nome],
        ['Estimativa', o.valorEstimado == null ? undefined : reais(o.valorEstimado)],
        ['Próxima ação', o.proximaAcao ? `${o.proximaAcao}${o.proximaAcaoEm ? ` · ${dataHora(o.proximaAcaoEm)}` : ''}` : 'nenhuma'],
      ]} />
    ),
    acoes: <Link className="botao secundario" to={`/comercial/oportunidade/${o.id}`}>Abrir a oportunidade</Link>,
  };
}

export function Oportunidades() {
  const { pode } = useSessao();
  const diretoria = pode('comercial.diretoria');
  const navegar = useNavigate();
  const [busca, setBusca] = useSearchParams();
  const estagio = busca.get('estagio');
  const linha = busca.get('linha');
  const responsavel = diretoria ? busca.get('responsavel') : null;
  const indicadas = busca.get('indicadas') === '1';
  const [texto, setTexto] = useState('');
  const [lista, setLista] = useState<ItemDaOportunidade[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [responsaveis, setResponsaveis] = useState<Pessoa[]>([]);
  const [nova, setNova] = useState(false);
  const pedido = useRef(0);

  const mudar = (chave: (typeof PARAMETROS)[number], valor: string | null) => {
    const b = new URLSearchParams(busca);
    if (valor === null) b.delete(chave); else b.set(chave, valor);
    setBusca(b, { replace: true });
  };

  const buscar = useCallback(async () => {
    const n = ++pedido.current;
    const q = new URLSearchParams();
    if (estagio) q.set('estagio', estagio);
    if (linha) q.set('linha', linha);
    if (responsavel) q.set('responsavel', responsavel);
    if (indicadas) q.set('indicadasPorMim', '1');
    try {
      const r = await api<ItemDaOportunidade[]>(`/comercial/oportunidades?${q}`);
      if (n !== pedido.current) return;
      setLista(r);
      setErro(null);
    } catch (e) {
      if (n === pedido.current) setErro(e);
    }
  }, [estagio, linha, responsavel, indicadas]);

  useEffect(() => { void buscar(); }, [buscar]);
  useEffect(() => {
    if (!diretoria) return;
    api<Pessoa[]>('/comercial/oportunidades/responsaveis').then(setResponsaveis).catch(() => setResponsaveis([]));
  }, [diretoria]);

  const adiada = useRef<number | null>(null);
  const tempoReal = useTempoReal(['comercial.funil'], () => {
    if (adiada.current !== null) return;
    adiada.current = window.setTimeout(() => { adiada.current = null; void buscar(); }, 300);
  });
  useEffect(() => () => { if (adiada.current !== null) window.clearTimeout(adiada.current); }, []);

  const visiveis = useMemo(() => {
    const q = semAcento(texto.trim());
    return (lista ?? []).filter((o) => !q || semAcento(
      `${o.contato.nome} ${o.cliente?.nome ?? ''} ${o.local.municipio ?? ''}`).includes(q));
  }, [lista, texto]);
  const pag = usePaginacao(visiveis, 50, `${texto}|${estagio}|${linha}|${responsavel}|${indicadas}`);

  const filtrosAtivos: FiltroAtivo[] = [
    ...(estagio ? [{ chave: 'estagio', texto: `Estágio: ${degrauDe(ESTAGIO_DA_OPORTUNIDADE, estagio).texto}`,
      tirar: () => mudar('estagio', null) }] : []),
    ...(linha ? [{ chave: 'linha', texto: `Linha: ${textoDe(LINHA, linha)}`, tirar: () => mudar('linha', null) }] : []),
    ...(responsavel ? [{ chave: 'responsavel',
      texto: `Responsável: ${responsaveis.find((p) => p.id === responsavel)?.nome ?? '…'}`,
      tirar: () => mudar('responsavel', null) }] : []),
    ...(texto.trim() ? [{ chave: 'busca', texto: `«${texto.trim()}»`, tirar: () => setTexto('') }] : []),
  ];
  const ativosNaFolha = (estagio ? 1 : 0) + (linha ? 1 : 0) + (responsavel ? 1 : 0) + (indicadas ? 1 : 0);
  const veValor = diretoria || (lista ?? []).some((o) => o.valorEstimado !== undefined);

  return (
    <div className="op-lista-tela">
      <header className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Oportunidades</h1>
          <p className="sub">
            {diretoria ? 'Todos os pedidos do funil, do mais urgente ao mais novo.'
              : 'Os pedidos de que você cuida e os que você indicou.'}
          </p>
        </div>
        {pode('crm.editar') && (
          <button type="button" className="botao" onClick={() => setNova(true)}>Nova oportunidade</button>
        )}
      </header>

      <BarraDeLista rotulo="Filtros das oportunidades" ativos={ativosNaFolha} total={visiveis.length}
        aoLimpar={() => setBusca(new URLSearchParams(), { replace: true })}>
        <BuscaDaBarra valor={texto} aoMudar={setTexto} placeholder="Contato, cliente ou município"
          rotulo="Buscar nas oportunidades" />
        <AlternarDaBarra ligado={indicadas} aoMudar={(v) => mudar('indicadas', v ? '1' : null)}>
          Indicadas por mim
        </AlternarDaBarra>
        <Seletor naBarra rotulo="Estágio" valor={estagio} todos="Todos"
          opcoes={[...ESTAGIOS_DO_QUADRO, 'descartada'].map((e) => ({ valor: e, texto: degrauDe(ESTAGIO_DA_OPORTUNIDADE, e).texto }))}
          aoMudar={(v) => mudar('estagio', v)} />
        <Seletor naBarra rotulo="Linha" valor={linha} todos="Todas as linhas"
          opcoes={Object.entries(LINHA).map(([valor, t]) => ({ valor, texto: t }))} aoMudar={(v) => mudar('linha', v)} />
        {diretoria && (
          <Seletor naBarra rotulo="Responsável" valor={responsavel} todos="Todos"
            opcoes={responsaveis.map((p) => ({ valor: p.id, texto: p.nome }))} aoMudar={(v) => mudar('responsavel', v)} />
        )}
      </BarraDeLista>
      <FiltrosAtivos itens={filtrosAtivos}
        aoLimpar={() => { setTexto(''); setBusca(new URLSearchParams(), { replace: true }); }}
        contagem={lista ? `${visiveis.length} ${visiveis.length === 1 ? 'oportunidade' : 'oportunidades'}` : undefined} />
      <AtualizacaoPausada estado={tempoReal} />

      {erro && !lista ? <ErroDaApi erro={erro} />
        : !lista ? <Carregando texto="Carregando as oportunidades" />
          : visiveis.length === 0 ? (
            <Vazio>
              {texto.trim() || ativosNaFolha
                ? 'Nenhuma oportunidade com estes filtros. Tire um filtro ou mude a busca.'
                : 'Nenhuma oportunidade ainda. O pedido que chega pela caixa de entrada vira oportunidade sozinho; o que '
                  + 'chegou por fora, crie em «Nova oportunidade».'}
            </Vazio>
          ) : (
            <div className="cartao op-lista">
              <TabelaComFicha
                linhas={pag.visiveis}
                chave={(o) => o.id}
                nome={(o) => `Oportunidade de ${nomeDoContato(o.contato.nome)}`}
                rotulo="Oportunidades"
                colunas={colunas(veValor)}
                ficha={ficha}
                destino={(o) => `/comercial/oportunidade/${o.id}`}
                cartao={(o) => {
                  const e = degrauDe(ESTAGIO_DA_OPORTUNIDADE, o.estagio);
                  return (
                    <span className="op-cartao-celular">
                      <span className="op-cartao-topo"><strong>{nomeDoContato(o.contato.nome)}</strong><Selo tom={e.tom}>{e.texto}</Selo></span>
                      <span className="sub">{[o.cliente?.nome, o.linha ? textoDe(LINHA, o.linha) : null,
                        localDaObra({ municipio: o.local.municipio, uf: o.local.uf })].filter(Boolean).join(' · ')}</span>
                      <span className="sub">{o.responsavel?.nome ?? 'ninguém ainda'}</span>
                    </span>
                  );
                }}
              />
              <RodapeDaLista {...pag} teto={500} />
            </div>
          )}

      <JanelaNovaOportunidade aberta={nova} aoFechar={() => setNova(false)}
        aoCriar={(id) => { setNova(false); navegar(`/comercial/oportunidade/${id}`); }} />
    </div>
  );
}
